import type { BlogPostDraft } from '../../lib/blog';

export const post: BlogPostDraft = {
  slug: 'tarifs-ugc',
  lang: 'fr',
  title: 'Tarifs UGC en 2026 : combien facturer une vidéo et ses droits ?',
  description:
    'Découvre comment fixer ton tarif UGC, facturer les hooks et les droits publicitaires et construire une proposition claire pour les marques.',
  date: '2026-08-19',
  author: 'Équipe Leadsme',
  tags: ['tarifs', 'ugc', 'droits', 'collaborations'],
  ctaTitleKey: 'blog.postCtaTrackTitle',
  ctaDescriptionKey: 'blog.postCtaTrackDescription',
  ctaButtonKey: 'landing.ctaButton',
  ctaTrustKey: 'blog.postCtaTrackTrust',
  content: `
<p class="blog-lead">
  Fixer ses tarifs fait partie des étapes les plus difficiles lorsque l'on se lance dans l'UGC. On trouve des grilles très différentes en ligne, certaines marques proposent directement un budget et d'autres demandent simplement&nbsp;: «&nbsp;Quels sont tes tarifs&nbsp;?&nbsp;»
</p>
<p>
  Il n'existe pourtant pas de prix officiel pour une vidéo UGC. Deux contenus de 30&nbsp;secondes peuvent demander un temps de travail très différent selon le brief, le nombre de scènes, le montage ou l'utilisation prévue par la marque.
</p>
<p>
  L'objectif n'est donc pas de trouver un tarif valable pour toutes les collaborations. Il s'agit plutôt de construire un prix de base, puis de l'adapter aux livrables et aux droits demandés.
</p>
<p>
  Tu cherches encore des entreprises à contacter&nbsp;? Découvre nos <a href="/blog/trouver-marques-ugc">5&nbsp;pistes concrètes pour trouver des marques en UGC</a>.
</p>

<h2>Quelle différence entre création UGC et influence&nbsp;?</h2>
<p>
  En UGC, la marque te rémunère principalement pour créer un contenu qu'elle pourra diffuser sur ses propres supports. Tu ne vends pas nécessairement une publication sur ton compte ni l'accès à ton audience.
</p>
<p>
  Ton nombre d'abonnés ne doit donc pas être le seul élément utilisé pour fixer ton prix. Ton expérience, la qualité de tes vidéos, ta capacité à suivre un brief et le temps nécessaire à la production sont généralement plus importants.
</p>
<p>
  Si la marque souhaite aussi que tu publies le contenu sur ton compte, cette prestation doit être distinguée. Son prix dépendra alors notamment de ton audience, de ton engagement et du format demandé.
</p>

<h2>Combien facturer une vidéo UGC en 2026&nbsp;?</h2>
<p>
  Pour une vidéo verticale simple de 15 à 45&nbsp;secondes destinée aux réseaux organiques d'une marque, on retrouve souvent des tarifs situés entre 150&nbsp;€ et 300&nbsp;€&nbsp;HT.
</p>
<p>
  Un profil qui débute peut se situer en dessous de cette fourchette, tandis qu'un créateur expérimenté ou spécialisé peut facturer 300&nbsp;€, 500&nbsp;€ ou davantage.
</p>
<p>
  Ces montants restent des repères, pas une règle. Une vidéo face caméra tournée dans un seul décor ne demande pas le même travail qu'une recette, une démonstration détaillée ou un contenu avec plusieurs lieux et acteurs.
</p>
<p>Le tarif de base peut généralement comprendre&nbsp;:</p>
<ul>
  <li>la lecture du brief et les échanges avec la marque</li>
  <li>la préparation du tournage et le script</li>
  <li>le tournage et le montage d'une version finale</li>
  <li>les sous-titres</li>
  <li>un nombre défini de retours</li>
  <li>une utilisation organique précisée dans la proposition</li>
</ul>
<p>
  Indique clairement ce qui est compris. La marque ne doit pas découvrir après le tournage que le script, les sous-titres ou les modifications étaient facturés séparément.
</p>

<h2>Comment construire son tarif de base&nbsp;?</h2>
<p>
  Commence par estimer le temps réellement consacré à une vidéo, y compris les tâches qui ne se voient pas dans le résultat final.
</p>
<p>Par exemple&nbsp;:</p>
<ul>
  <li>30&nbsp;minutes pour les échanges et la lecture du brief</li>
  <li>1&nbsp;heure pour le script et la préparation</li>
  <li>2&nbsp;heures pour le tournage</li>
  <li>2&nbsp;heures pour le montage et les sous-titres</li>
  <li>30&nbsp;minutes pour les modifications et l'envoi</li>
</ul>
<p>
  Une vidéo de 30&nbsp;secondes peut ainsi représenter six heures de travail.
</p>
<p>
  Ton tarif doit également tenir compte de tes charges, de ton matériel, de tes logiciels et du temps consacré à la gestion de ton activité. Si tu reprends simplement le tarif d'un autre créateur, tu risques d'oublier une partie de tes propres coûts.
</p>

<h2>Quels suppléments peut-on facturer&nbsp;?</h2>
<p>
  Une demande supplémentaire paraît parfois rapide, mais elle peut nécessiter une nouvelle préparation, un nouveau tournage ou plusieurs exports.
</p>
<p>Pense notamment à chiffrer séparément&nbsp;:</p>
<ul>
  <li>un hook ou un appel à l'action supplémentaire</li>
  <li>une variation du script ou du montage</li>
  <li>une version plus courte de la vidéo</li>
  <li>les rushs bruts ou le fichier source</li>
  <li>une deuxième langue</li>
  <li>des photos en complément</li>
  <li>la présence d'un autre acteur</li>
  <li>l'achat d'accessoires ou d'ingrédients</li>
  <li>un déplacement ou une livraison urgente</li>
  <li>des retours supplémentaires</li>
  <li>une exclusivité avec la marque ou son secteur</li>
</ul>
<p>
  Un hook supplémentaire peut être proposé sous forme de forfait. Son prix dépendra notamment de la nécessité de tourner une simple phrase ou de créer une nouvelle mise en scène.
</p>
<p>
  Les rushs ne doivent pas non plus être ajoutés automatiquement. Ils peuvent permettre à la marque de créer plusieurs contenus à partir de ton tournage. Précise les fichiers transmis et les usages autorisés.
</p>

<h2>Comment facturer les droits d'utilisation&nbsp;?</h2>
<p>
  Le tarif de création rémunère ton travail de production. Les droits d'utilisation déterminent comment, où et pendant combien de temps la marque pourra exploiter la vidéo.
</p>
<p>Il faut notamment distinguer&nbsp;:</p>
<ul>
  <li>la diffusion organique sur les comptes de la marque</li>
  <li>l'utilisation en publicité sur Meta, TikTok ou une autre plateforme</li>
  <li>la diffusion sur un site, une newsletter ou une marketplace</li>
  <li>la durée et les pays concernés</li>
  <li>le droit de modifier ou de décliner la vidéo</li>
  <li>l'utilisation depuis ton compte, comme les Spark Ads</li>
</ul>
<p>
  Le paiement de la vidéo ne donne pas automatiquement à la marque une utilisation publicitaire illimitée. Les supports, la durée et le territoire doivent être définis dans la proposition ou le contrat.
</p>
<p>
  Pour les publicités, certains créateurs appliquent un pourcentage du tarif de création pour chaque mois d'utilisation. D'autres proposent un forfait de trois, six ou douze mois.
</p>
<p>
  Imaginons une vidéo facturée 250&nbsp;€&nbsp;HT. Si tu choisis d'appliquer 20&nbsp;% du tarif de création par mois, trois mois de droits publicitaires représentent 150&nbsp;€ supplémentaires&nbsp;:
</p>
<ul>
  <li>création de la vidéo&nbsp;: 250&nbsp;€&nbsp;HT</li>
  <li>droits publicitaires pendant trois mois&nbsp;: 150&nbsp;€&nbsp;HT</li>
  <li>total&nbsp;: 400&nbsp;€&nbsp;HT</li>
</ul>
<p>
  Ce pourcentage est un exemple de méthode, pas un tarif obligatoire. L'important est que la marque comprenne ce qu'elle paie et qu'une date de fin soit prévue.
</p>
<p>
  Si elle prolonge sa campagne, tu peux facturer un renouvellement. Une utilisation mondiale, sur tous les supports et sans limite de durée doit être évaluée avec prudence, car elle ne te permet plus de renégocier les droits.
</p>

<h2>Comment proposer un pack de plusieurs vidéos&nbsp;?</h2>
<p>
  Un pack peut permettre de regrouper les échanges, la préparation et parfois le tournage. Tu peux proposer une réduction raisonnable lorsque cette organisation te fait réellement gagner du temps.
</p>
<p>
  Évite toutefois de diviser ton prix simplement parce que la marque commande davantage. Trois vidéos nécessitent toujours trois montages et parfois des concepts ou des décors différents.
</p>
<p>
  Avant d'annoncer le prix, vérifie le nombre de vidéos, de hooks, de jours de tournage et les droits prévus pour chaque contenu. Les droits publicitaires peuvent apparaître sur une ligne séparée afin de distinguer la valeur de la création de celle de la diffusion.
</p>

<h2>Quelles questions poser avant d'envoyer son prix&nbsp;?</h2>
<p>
  Un tarif envoyé trop rapidement repose souvent sur des informations incomplètes.
</p>
<p>Avant de préparer ta proposition, demande à la marque&nbsp;:</p>
<ul>
  <li>combien de contenus sont prévus et quelle doit être leur durée&nbsp;?</li>
  <li>qui prépare les scripts&nbsp;?</li>
  <li>combien de hooks et d'appels à l'action sont demandés&nbsp;?</li>
  <li>les rushs doivent-ils être transmis&nbsp;?</li>
  <li>combien de retours sont prévus&nbsp;?</li>
  <li>où les vidéos seront-elles diffusées&nbsp;?</li>
  <li>seront-elles utilisées en publicité, pendant combien de temps et dans quels pays&nbsp;?</li>
  <li>existe-t-il une exclusivité&nbsp;?</li>
  <li>quelle est la date de livraison souhaitée&nbsp;?</li>
</ul>
<p>
  Tu n'as pas besoin d'envoyer une grille de plusieurs pages. Une proposition courte, détaillant les livrables, les droits, le délai et le prix, sera souvent plus facile à comprendre.
</p>

<h2>Comment présenter son tarif à une marque&nbsp;?</h2>
<p>Tu peux utiliser une formulation simple&nbsp;:</p>
<blockquote>
  <p>Pour la création d'une vidéo UGC comprenant la préparation, le tournage, le montage et un aller-retour de modifications, mon tarif est de 250&nbsp;€&nbsp;HT. Les droits d'utilisation publicitaire sont proposés séparément selon la durée de diffusion souhaitée.</p>
</blockquote>
<p>
  Si plusieurs prestations sont demandées, présente chaque élément sur une ligne distincte. Cela permet d'ajuster le projet sans donner l'impression que ton prix a été choisi au hasard.
</p>
<p>
  Évite de proposer immédiatement une réduction lorsqu'une marque indique que ton tarif dépasse son budget. Demande plutôt quel montant est prévu et adapte les livrables, la durée des droits ou le nombre de vidéos.
</p>

<h2>Comment suivre ses tarifs et ses droits&nbsp;?</h2>
<p>
  Lorsque les collaborations se multiplient, il devient difficile de se souvenir du montant négocié, de la durée des droits ou de la date prévue pour le paiement.
</p>
<p>
  Pour chaque projet, conserve le tarif accepté, les suppléments, les supports autorisés, la durée des droits, la date de livraison et l'échéance du paiement.
</p>
<p>
  Avec Leadsme, tu peux centraliser tes marques, suivre tes projets et garder une vue claire sur les montants facturés et les paiements attendus.
</p>
<p>
  <a href="https://leadsme.app">Créer gratuitement mon espace Leadsme</a>
</p>

<h2>Pour résumer</h2>
<p>
  Il n'existe pas de tarif UGC unique. Pour une vidéo simple, appuie-toi sur les prix observés, mais surtout sur ton temps de travail, tes coûts et la complexité du brief.
</p>
<p>
  Sépare toujours la création, les prestations supplémentaires et les droits d'utilisation. Demande où la vidéo sera diffusée, pendant combien de temps et si elle sera utilisée en publicité avant d'envoyer ton prix.
</p>
<p>
  Enfin, détaille clairement ta proposition. Une marque acceptera plus facilement un tarif lorsqu'elle comprend précisément les livrables et les utilisations qu'il couvre.
</p>
`,
};
